import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SelectOption } from '@scaleaq/scaleui';

import { AspectRatio, type WebRtcStream } from '@scaleaq/sitecontrol-lib';

import { useWhepPlayer } from '@/components/cameraStreamWidget/usePlayWhepSteam.ts';
import { capitalizeAndSplitOnUpperCase } from '@/utils';
import { addScaleInternalToUrl } from '@/utils/scaleInternal';
import { addViewerTagToUrl } from '@/utils/viewerTag';

type StreamOption = SelectOption<WebRtcStream>;
type PlayerIndex = 0 | 1;
type PlayerStreams = [StreamOption | undefined, StreamOption | undefined];

/** Drop a make-before-break attempt that never reaches `playing` so the toggle is not latched. */
const PROFILE_SWITCH_READY_TIMEOUT_MS = 10_000;

type UseCameraStreamLivePlayersOptions = {
  cameraId: string | undefined;
  webRtcStreams: WebRtcStream[] | undefined;
  persistedWebRtcStreamName: string | undefined;
  preferredLowResolutionStreamName?: string;
  preferLowResolutionOnInitialLoad: boolean;
  onPersistWebRtcStreamName?: (streamName: string | undefined) => void;
  jitterBufferTargetMs: number | undefined;
  includeScaleInternal: boolean | undefined;
  /** When false, both WHEP sessions are torn down (pause / HLS rewind). */
  enabled: boolean;
};

/**
 * Make-before-break live WHEP: keep the current profile on one player until the other
 * is `playing`, then swap and drop the previous session.
 */
export const useCameraStreamLivePlayers = ({
  cameraId,
  webRtcStreams,
  persistedWebRtcStreamName,
  preferredLowResolutionStreamName,
  preferLowResolutionOnInitialLoad,
  onPersistWebRtcStreamName,
  jitterBufferTargetMs,
  includeScaleInternal,
  enabled,
}: UseCameraStreamLivePlayersOptions) => {
  const streamOptions = useMemo(() => webRtcStreamSelectOptions(webRtcStreams), [webRtcStreams]);

  const [playerStreams, setPlayerStreams] = useState<PlayerStreams>([undefined, undefined]);
  const [activePlayerIndex, setActivePlayerIndex] = useState<PlayerIndex>(0);

  const playbackRef = useRef({ playerStreams, activePlayerIndex });
  playbackRef.current = { playerStreams, activePlayerIndex };

  const selectedStream = playerStreams[activePlayerIndex];
  const pendingPlayerIndex = otherPlayerIndex(activePlayerIndex);
  const isProfileSwitching = Boolean(playerStreams[pendingPlayerIndex]);

  useEffect(() => {
    if (!streamOptions?.length) {
      setPlayerStreams([undefined, undefined]);
      setActivePlayerIndex(0);
      return;
    }

    const { playerStreams: slots, activePlayerIndex: active } = playbackRef.current;
    const nextStream = resolveSelectedWebRtcStreamOption(
      streamOptions,
      persistedWebRtcStreamName,
      slots[active],
      preferredLowResolutionStreamName,
      preferLowResolutionOnInitialLoad
    );
    if (slots[active]?.value.webRtcStreamUri === nextStream.value.webRtcStreamUri) {
      setPlayerStreams(current => replaceSlot(current, active, nextStream));
      return;
    }

    setPlayerStreams([nextStream, undefined]);
    setActivePlayerIndex(0);
  }, [
    /* effect dep */ cameraId,
    streamOptions,
    persistedWebRtcStreamName,
    preferredLowResolutionStreamName,
    preferLowResolutionOnInitialLoad,
  ]);

  const firstPlayer = useSlotWhepPlayer(
    playerStreams[0]?.value,
    includeScaleInternal,
    jitterBufferTargetMs,
    enabled
  );
  const secondPlayer = useSlotWhepPlayer(
    playerStreams[1]?.value,
    includeScaleInternal,
    jitterBufferTargetMs,
    enabled
  );
  const players = [firstPlayer, secondPlayer] as const;
  const activePlayer = players[activePlayerIndex];
  const pendingPlayer = isProfileSwitching ? players[pendingPlayerIndex] : undefined;

  const onProfileToggle = useCallback((stream: StreamOption) => {
    const { playerStreams: slots, activePlayerIndex: active } = playbackRef.current;
    const pending = otherPlayerIndex(active);
    if (slots[pending] || stream.value.webRtcStreamUri === slots[active]?.value.webRtcStreamUri) {
      return;
    }
    setPlayerStreams(current => replaceSlot(current, pending, stream));
  }, []);

  const onPlayerReady = useCallback(
    (playerIndex: PlayerIndex) => {
      const { playerStreams: slots, activePlayerIndex: active } = playbackRef.current;
      const stream = slots[playerIndex];
      if (playerIndex === active || !stream) return;

      setActivePlayerIndex(playerIndex);
      setPlayerStreams(current => replaceSlot(current, active, undefined));
      onPersistWebRtcStreamName?.(stream.value.name);
    },
    [onPersistWebRtcStreamName]
  );

  /** Clear the standby slot only — the live player keeps playing. */
  const onPlayerFailed = useCallback((playerIndex: PlayerIndex) => {
    const { playerStreams: slots, activePlayerIndex: active } = playbackRef.current;
    if (playerIndex === active || !slots[playerIndex]) return;
    setPlayerStreams(current => replaceSlot(current, playerIndex, undefined));
  }, []);

  useEffect(() => {
    if (!isProfileSwitching || pendingPlayer?.state !== 'failed') return;
    onPlayerFailed(pendingPlayerIndex);
  }, [isProfileSwitching, pendingPlayerIndex, pendingPlayer?.state, onPlayerFailed]);

  // Start once the standby slot is occupied. Do not depend on ICE state:
  // useWhepPlayer retries cycle new → connecting → failed → new, which restarted
  // the timer on every hop so the toggle never re-enabled.
  useEffect(() => {
    if (!isProfileSwitching) return;
    const timeoutId = window.setTimeout(
      () => onPlayerFailed(pendingPlayerIndex),
      PROFILE_SWITCH_READY_TIMEOUT_MS
    );
    return () => window.clearTimeout(timeoutId);
  }, [isProfileSwitching, pendingPlayerIndex, onPlayerFailed]);

  return {
    streamOptions,
    selectedStream,
    playerStreams,
    firstPlayer,
    secondPlayer,
    activePlayerIndex,
    isProfileSwitching,
    onProfileToggle,
    onPlayerReady,
    onPlayerFailed,
    videoRef: activePlayer.videoRef,
    state: activePlayer.state,
    error: activePlayer.error,
    errorCode: activePlayer.errorCode,
    isLoading: activePlayer.isLoading,
    retryCount: activePlayer.retryCount,
  };
};

const useSlotWhepPlayer = (
  stream: WebRtcStream | undefined,
  includeScaleInternal: boolean | undefined,
  jitterBufferTargetMs: number | undefined,
  enabled: boolean
) => {
  const url = getWebRtcUrl(stream, includeScaleInternal);
  return useWhepPlayer({
    url,
    streamUri: stream?.webRtcStreamUri,
    jitterBufferTargetMs,
    enabled: enabled && Boolean(url),
  });
};

const otherPlayerIndex = (index: PlayerIndex): PlayerIndex => (index === 0 ? 1 : 0);

const replaceSlot = (
  current: PlayerStreams,
  index: PlayerIndex,
  stream: StreamOption | undefined
): PlayerStreams => {
  if (current[index] === stream) return current;
  const next: PlayerStreams = [...current];
  next[index] = stream;
  return next;
};

const getWebRtcUrl = (
  stream: WebRtcStream | undefined,
  includeScaleInternal: boolean | undefined
): string => {
  const url = stream?.webRtcStreamUri ?? '';
  // Tagged here, at the last step: the untagged URI is what identifies the stream.
  return addViewerTagToUrl(includeScaleInternal ? addScaleInternalToUrl(url) : url);
};

const webRtcStreamSelectOptions = (
  streams: WebRtcStream[] | undefined
): SelectOption<WebRtcStream>[] | undefined => {
  if (!streams?.length) return undefined;
  return streams.map(s => ({
    label: capitalizeAndSplitOnUpperCase(s.name),
    value: s,
    id: s.name,
  }));
};

const defaultWebRtcStreamOption = (
  options: SelectOption<WebRtcStream>[]
): SelectOption<WebRtcStream> => {
  return options.find(o => o.value.aspectRatio === AspectRatio.Standard) ?? options[0];
};

export const resolveSelectedWebRtcStreamOption = (
  streamOptions: SelectOption<WebRtcStream>[],
  persistedWebRtcStreamName: string | undefined,
  prev: SelectOption<WebRtcStream> | undefined,
  preferredLowResolutionStreamName: string | undefined,
  preferLowResolutionOnInitialLoad: boolean
): SelectOption<WebRtcStream> => {
  // Layout / saved preference (name only; URI comes from current camera API).
  if (persistedWebRtcStreamName) {
    const match = streamOptions.find(o => o.value.name === persistedWebRtcStreamName);
    if (match) return match;
  }
  // In-session choice when streams refresh but camera id unchanged (e.g. refetch).
  const prevUri = prev?.value.webRtcStreamUri;
  if (prev && prevUri && streamOptions.some(o => o.value.webRtcStreamUri === prevUri)) return prev;
  if (preferLowResolutionOnInitialLoad) {
    return lowestResolutionWebRtcStreamOption(streamOptions, preferredLowResolutionStreamName);
  }
  // First paint or no match: standard aspect ratio, else first stream.
  return defaultWebRtcStreamOption(streamOptions);
};

const lowestResolutionWebRtcStreamOption = (
  options: SelectOption<WebRtcStream>[],
  preferredName: string | undefined
): SelectOption<WebRtcStream> => {
  if (preferredName) {
    const normalizedName = preferredName.toUpperCase();
    const configuredLowStream = options.find(
      option => option.value.name.toUpperCase() === normalizedName
    );
    if (configuredLowStream) return configuredLowStream;
  }

  const namedLowStream = options.find(option =>
    /(^|[^A-Z])(LOW|LQ|LO)([^A-Z]|$)/.test(option.value.name.toUpperCase())
  );
  if (namedLowStream) return namedLowStream;

  return options.reduce((lowest, candidate) =>
    compareStreamResolution(candidate.value, lowest.value) < 0 ? candidate : lowest
  );
};

const compareStreamResolution = (a: WebRtcStream, b: WebRtcStream): number => {
  const pixelDifference = streamPixelCount(a) - streamPixelCount(b);
  return pixelDifference || a.bitRate - b.bitRate;
};

const streamPixelCount = (stream: WebRtcStream): number =>
  stream.width > 0 && stream.height > 0 ? stream.width * stream.height : Number.POSITIVE_INFINITY;
